import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Lock, Play } from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import { useReveal } from "@/hooks/useReveal";
import { LIBRARY_VIDEOS } from "@/data/biblioteca";

// Editorial magazine composition — not a grid, an asymmetric layout that flows.
function EditorialVideoCard({ video, index, size = "regular" }) {
  const aspect = size === "large" ? "aspect-[16/11]" : size === "tall" ? "aspect-[3/4]" : "aspect-[4/5]";
  const rn = ["i", "ii", "iii", "iv", "v", "vi"][index] || "";

  return (
    <article className="reveal group" data-testid={`library-video-${video.id}`}>
      <div
        className="relative overflow-hidden shadow-[0_18px_60px_-30px_rgba(31,28,21,0.35)]"
        style={{ borderRadius: "var(--radius-organic)" }}
      >
        <img
          src={video.poster}
          alt=""
          loading="lazy"
          className={`w-full ${aspect} object-cover transition-transform duration-[1600ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]`}
          style={{ filter: "grayscale(0.35) contrast(1.02)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/55 pointer-events-none" />
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(255,222,29,0.18) 0%, transparent 65%)",
          }}
        />

        <span
          className="absolute top-7 left-7 font-display italic text-2xl text-white/85"
          aria-hidden="true"
        >
          {rn}
        </span>

        <div className="absolute top-7 right-7 w-12 h-12 rounded-full border border-white/55 bg-white/8 backdrop-blur-md flex items-center justify-center transition-all duration-700 group-hover:bg-white group-hover:border-white">
          <Play
            size={14}
            strokeWidth={1.2}
            className="text-white ml-0.5 transition-colors duration-500 group-hover:text-[var(--luft-ink)]"
            fill="currentColor"
          />
        </div>

        <div className="absolute bottom-0 inset-x-0 p-7 flex items-end justify-between">
          <div>
            <p
              className="text-white/70"
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: "0.56rem",
                letterSpacing: "0.32em",
                textTransform: "uppercase",
              }}
            >
              {video.teacher}
            </p>
            <p className="font-display italic text-white text-xl mt-1.5">
              {video.duration}
            </p>
          </div>
          <p
            className="text-white/72 text-right"
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "0.58rem",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
            }}
          >
            {video.level}
          </p>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="editorial-heading text-2xl sm:text-[1.6rem] text-[var(--luft-ink)] leading-tight">
          {video.title}
        </h3>
        <p className="mt-3 text-[var(--luft-ink-soft)] font-body font-light text-[0.93rem] leading-relaxed max-w-sm">
          {video.description}
        </p>
      </div>
    </article>
  );
}

export default function Online() {
  const navigate = useNavigate();
  const ref = useReveal();
  const [featured, second, third, fourth] = LIBRARY_VIDEOS;

  return (
    <main className="bg-[var(--luft-bg)]" ref={ref} data-testid="online-page">
      <Navigation />

      {/* Hero */}
      <section
        className="relative w-full min-h-[80svh] flex items-end pt-32 pb-24 px-6 sm:px-10 lg:px-16 overflow-hidden bg-[var(--luft-bg)]"
        data-testid="online-hero"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute -top-32 -right-20 w-[55vw] h-[55vw] rounded-full opacity-[0.30]"
            style={{
              background:
                "radial-gradient(circle, var(--luft-sky) 0%, transparent 70%)",
            }}
          />
          <div
            className="absolute -bottom-40 -left-24 w-[50vw] h-[50vw] rounded-full opacity-[0.20]"
            style={{
              background:
                "radial-gradient(circle, var(--luft-blue) 0%, transparent 72%)",
            }}
          />
          <div
            className="absolute top-1/3 left-1/2 w-[38vw] h-[38vw] rounded-full opacity-[0.10]"
            style={{
              background:
                "radial-gradient(circle, var(--luft-yellow) 0%, transparent 70%)",
            }}
          />
        </div>

        <div className="relative max-w-[1440px] mx-auto w-full">
          <div className="flex items-center justify-between mb-14">
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-[var(--luft-ink-soft)] text-[0.7rem] uppercase tracking-[0.28em] hover:text-[var(--luft-blue)] transition-colors"
              data-testid="online-back-btn"
            >
              <ArrowLeft size={14} strokeWidth={1.4} />
              voltar ao Lu.ft
            </button>

            <button
              onClick={() => navigate("/online/members")}
              className="inline-flex items-center gap-2.5 text-[var(--luft-ink)] text-[0.7rem] uppercase tracking-[0.28em] border-b border-[var(--luft-ink)]/35 pb-1.5 hover:text-[var(--luft-blue)] hover:border-[var(--luft-blue)] transition-colors"
              data-testid="online-members-entry"
            >
              <Lock size={12} strokeWidth={1.4} />
              Entrar
            </button>
          </div>

          <p className="eyebrow mb-8">Biblioteca Lu.ft</p>
          <h1
            className="editorial-heading text-[var(--luft-ink)] text-[clamp(3rem,9vw,8rem)] leading-[0.94] max-w-5xl"
            data-testid="online-hero-title"
          >
            A tua prática,<br />
            <em className="editorial-italic">onde estiveres</em>.
          </h1>
          <p className="mt-10 max-w-xl font-body font-light text-[var(--luft-ink-soft)] text-[1.02rem] leading-relaxed">
            Uma extensão do estúdio Lu.ft, feita para continuar a prática em
            casa, em viagem ou entre aulas. Aulas curtas, filmadas em luz
            natural, criadas com o mesmo cuidado do estúdio.
          </p>
        </div>
      </section>

      {/* Editorial intro */}
      <section className="relative bg-[var(--luft-bg-alt)] py-28 sm:py-36 lg:py-44 overflow-hidden">
        <div className="relative max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-12 gap-y-12 lg:gap-x-12">
          <div className="col-span-12 lg:col-span-4 reveal">
            <p className="eyebrow mb-6">01 — A ideia</p>
            <p className="font-display italic text-[var(--luft-ink-soft)] text-2xl mt-8 leading-snug">
              Continuar a prática,<br />sem perder o cuidado.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-8 reveal">
            <h2 className="editorial-heading text-[clamp(2rem,4.4vw,3.4rem)] text-[var(--luft-ink)] leading-[1.02]">
              Uma biblioteca<br />
              <em className="editorial-italic">em crescimento</em>.
            </h2>
            <div className="mt-10 grid sm:grid-cols-2 gap-8 text-[var(--luft-ink-soft)] font-body font-light text-[1rem] leading-relaxed">
              <p>
                Práticas curtas para os dias em que fica em casa, para levar
                consigo quando viaja ou simplesmente para manter o corpo em
                movimento entre aulas.
              </p>
              <p>
                Filmada com o mesmo cuidado editorial do estúdio: luz natural,
                voz calma, sem música a competir. Apenas movimento, respiração
                e presença.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial video composition */}
      <section
        className="relative bg-[var(--luft-bg)] py-28 sm:py-36 lg:py-44 overflow-hidden"
        data-testid="library-videos"
      >
        <div className="relative max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="mb-20 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 reveal">
            <div>
              <p className="eyebrow mb-8">02 — Aulas</p>
              <h2 className="editorial-heading text-[clamp(2rem,4.8vw,3.8rem)] text-[var(--luft-ink)] leading-[1] max-w-2xl">
                Rituais <em className="editorial-italic">prontos</em><br />
                a viajar.
              </h2>
            </div>
            <p className="font-body font-light text-[var(--luft-ink-soft)] max-w-sm leading-relaxed">
              Cada vídeo é uma sequência curta, filmada em luz natural no
              estúdio Lu.ft. Novas aulas adicionadas todos os meses.
            </p>
          </div>

          {/* Asymmetric editorial grid */}
          <div className="grid grid-cols-12 gap-y-14 lg:gap-x-8">
            {/* Row 1: large left + tall right */}
            <div className="col-span-12 md:col-span-7">
              <EditorialVideoCard video={featured} index={0} size="large" />
            </div>
            <div className="col-span-12 md:col-span-5 md:mt-20">
              <EditorialVideoCard video={second} index={1} size="tall" />
            </div>

            {/* Row 2: offset small + medium */}
            <div className="col-span-12 md:col-span-5 md:col-start-2 md:-mt-6">
              <EditorialVideoCard video={third} index={2} size="regular" />
            </div>
            <div className="col-span-12 md:col-span-6 md:col-start-7 md:mt-16">
              <EditorialVideoCard video={fourth} index={3} size="tall" />
            </div>
          </div>
        </div>
      </section>

      {/* Members CTA strip */}
      <section
        className="relative bg-[var(--luft-ink)] text-[#F6F4EE] py-28 sm:py-36 overflow-hidden"
        data-testid="online-members-strip"
      >
        <div
          className="pointer-events-none absolute -top-40 -right-40 w-[60vw] h-[60vw] rounded-full opacity-[0.15]"
          style={{
            background:
              "radial-gradient(circle, var(--luft-sky) 0%, transparent 70%)",
          }}
        />
        <div className="relative max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-12 gap-y-10 lg:gap-x-14 items-end">
          <div className="col-span-12 lg:col-span-7 reveal">
            <p
              className="text-[#F6F4EE]/55 mb-7"
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: "0.66rem",
                letterSpacing: "0.32em",
                textTransform: "uppercase",
              }}
            >
              03 — Área privada
            </p>
            <h2 className="editorial-heading text-[#F6F4EE] text-[clamp(2rem,4.6vw,3.6rem)] leading-[1] max-w-2xl">
              Já é membro da{" "}
              <em
                className="editorial-italic"
                style={{ color: "var(--luft-yellow)" }}
              >
                Biblioteca Lu.ft
              </em>
              ?
            </h2>
            <p className="mt-8 max-w-md font-body font-light text-[#F6F4EE]/72 text-[1rem] leading-relaxed">
              A biblioteca é uma extensão dedicada aos alunos e alunas do
              estúdio. Utilize o seu código de acesso pessoal para entrar.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:pl-10 reveal">
            <div className="border-t border-white/15 pt-10">
              <button
                onClick={() => navigate("/online/members")}
                className="inline-flex items-center gap-3 text-[#F6F4EE] text-[0.72rem] uppercase tracking-[0.28em] border-b border-[#F6F4EE]/55 pb-2 hover:text-[var(--luft-yellow)] hover:border-[var(--luft-yellow)] transition-colors"
                data-testid="online-members-cta"
              >
                Entrar na Biblioteca
                <ArrowRight size={14} strokeWidth={1.4} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
